/*
 * lp-hero
 * ------------------------------------------------------------------
 * Instagram-style social video reel. The correct campaign reel for the URL's
 * search param is resolved BEFORE first paint by the inline script in
 * sections/lp-hero.liquid (Liquid can't read query params, and a deferred swap
 * would flash). Locale resolution is done server-side. By the time this runs the
 * card already shows the right reel.
 *
 * This element is the enhancement layer:
 *  - muted autoplay of the in-view reel (skipped under prefers-reduced-motion,
 *    leaving the poster as the still view),
 *  - fade the video in over the poster once it's actually playing,
 *  - pause when scrolled out of view / tab hidden,
 *  - the tap-to-unmute sound toggle (the only real control).
 */
class LpHero extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-reel-video]');
    if (!this.video) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Fade in only once actually playing — avoids a partial-frame flash and
    // leaves the poster as the reduced-motion view.
    this.video.addEventListener('playing', () => this.video.classList.add('is-ready'), { once: true });

    this.setupSound();

    // Autoplay/pause based on visibility (unless reduced motion).
    if (!this.reduceMotion) {
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => entries.forEach((e) => (e.isIntersecting ? this.play() : this.video.pause())),
          { threshold: 0.25 }
        );
        this.io.observe(this);
      } else {
        this.play();
      }
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.pause();
        else if (this.isInView()) this.play();
      });
    }
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }

  play() {
    const played = this.video.play && this.video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  }

  isInView() {
    const r = this.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
  }

  setupSound() {
    const btn = this.querySelector('[data-reel-sound]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const muted = !this.video.muted;
      this.video.muted = muted;
      btn.setAttribute('aria-pressed', String(!muted));
      // Unmuting is a user gesture, so sound is allowed; make sure it's playing.
      if (!muted) this.play();
    });
  }
}

if (!customElements.get('lp-hero')) {
  customElements.define('lp-hero', LpHero);
}
